/**
 * Redirects away from the not-certified screen when the patient doesn't belong there:
 * no insurance selected → insurance-select; certified course (or error) → normal explain path.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInsuranceExplainPath, resolveBillingTypeFromPatient } from '../insurance/routing';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';
import { useCourseCertified } from './useCourseCertified';

export function useCourseNotCertifiedGuard(): void {
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const courseId = signedUpPatient?.course?.id ?? '';
  const certified = useCourseCertified(courseId);

  useEffect(() => {
    if (!signedUpPatient) {
      return;
    }
    const billingType = resolveBillingTypeFromPatient(signedUpPatient);
    if (!billingType) {
      navigate('/webAppOnboarding/insurance-select', { replace: true });
      return;
    }
    if (certified.status === 'loading') {
      return;
    }
    const isCertified = certified.status !== 'ready' || certified.certified;
    const insuranceInfo = signedUpPatient.webAppOnboarding?.insuranceInfo;
    const practiceChargesFee = insuranceInfo?.practiceChargesFee === true;
    // This screen tells the patient the course costs them nothing. That is wrong when the practice
    // charges its own fee, so those patients belong on the fee screen even here (PF-5963).
    if (isCertified || practiceChargesFee) {
      navigate(
        getInsuranceExplainPath(billingType, { blockedReason: insuranceInfo?.blockedReason, practiceChargesFee }),
        { replace: true },
      );
    }
  }, [signedUpPatient, certified, navigate]);
}
